/**
 * @file 問題ごとの解説の面の本体 (問題一覧と、選んだ問題の解説)。
 *
 * @what: 左に節 (Theme) ごとの問題 (例題と CHECK!) を出題・解答のページへのリンクつきで並べ、
 *   右に選んだ問題の解説 (数式つき Markdown とグラフ) を出す。狭い画面では一覧を折りたたみ、
 *   解説を先に出す。ページのリンクは印刷ページに資料の reader_offset を足して連番にする。
 * @why: 参考書を問題1つの単位で引き、紙面で省かれた別解の具体や背景の図を補って読めるようにする
 *   (代表判断、2026-09-05)。問題文は載せず、原本のページを開く導線にとどめる。答案に書く形の模範解答は
 *   折りたたみにし、方針を読んだあと自分で書いてみる余地を残す (代表判断、2026-09-06)。
 * @failure: 描画専用である。解説の取得の状態 (読み込み中・失敗・無し) は親 (route) が渡し、ここは文言を出すだけ。
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import "katex/dist/katex.min.css";
import { FunctionPlot } from "../aids/lib/FunctionPlot";
import { renderMathMarkdown } from "../aids/lib/mathMarkdown";
import type { StudyProblemGuide, StudyWorkIndex, StudyWorkProblem } from "../api/studyWorks";
import { toPlotSpec } from "./guidePlot";

export type GuideState =
  | { status: "none" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; guide: StudyProblemGuide };

/** 模範解答の折りたたみの見出しの既定の文言 (YAML の title が無いとき)。 */
const DEFAULT_ANSWER_TITLE = "答案に書く形で見る";

/** 一覧を最初から開くか (広い画面 = 開く。jsdom 等 matchMedia が無い環境は開く)。 */
function wideScreen(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(min-width: 901px)").matches
    : true;
}

export function ProblemGuideView({
  index,
  readerHref,
  readerOffset,
  selectedId,
  onSelect,
  guide,
  below,
}: {
  index: StudyWorkIndex;
  /** 原本のリーダーの URL の頭 (…/sources/{sourceId})。?page= を付けてページへ飛ぶ。 */
  readerHref: string;
  readerOffset: number;
  selectedId: string | null;
  onSelect: (problemId: string) => void;
  guide: GuideState;
  /** 解説の下に出すもの (ふりかえりの欄など)。 */
  below?: ReactNode;
}) {
  const [listOpen, setListOpen] = useState(wideScreen);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 901px)");
    const onChange = (e: MediaQueryListEvent) => setListOpen(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const pageHref = (printed: number) => `${readerHref}?page=${printed + readerOffset}`;
  const problem = index.problems.find((p) => p.id === selectedId);

  return (
    <div className="study-guides">
      <details
        className="study-guides-list-wrap"
        open={listOpen}
        onToggle={(e) => setListOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="study-guides-list-summary">問題一覧</summary>
        <nav className="study-guides-list" aria-label="問題一覧">
          {index.sections.map((section) => (
            <section key={section.id} className="study-guides-theme">
              <h3 className="study-guides-theme-title">
                <Link to={pageHref(section.page)}>Theme{section.id}</Link>{" "}
                <span className="study-guides-theme-name">{section.title}</span>
              </h3>
              <ul className="study-guides-problems">
                {index.problems
                  .filter((p) => p.section_id === section.id)
                  .map((p) => (
                    <ProblemRow
                      key={p.id}
                      problem={p}
                      selected={p.id === selectedId}
                      pageHref={pageHref}
                      onOpen={() => onSelect(p.id)}
                    />
                  ))}
              </ul>
            </section>
          ))}
          {index.extras && index.extras.length > 0 ? (
            <section className="study-guides-theme">
              <h3 className="study-guides-theme-title">読みもの</h3>
              <ul className="study-guides-problems">
                {index.extras.map((d) => (
                  <li key={d.page} className="study-guides-problem">
                    <span className="study-guides-problem-label">{d.title}</span>
                    <span className="study-guides-problem-links">
                      <Link to={pageHref(d.page)}>p.{d.page}</Link>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </nav>
      </details>
      <div className="study-guides-body">
        {problem ? (
          <>
            <GuideBody problem={problem} guide={guide} pageHref={pageHref} />
            {below}
          </>
        ) : (
          <p className="study-muted">一覧から問題を選ぶと、ここに解説が出ます。</p>
        )}
      </div>
    </div>
  );
}

function ProblemRow({
  problem,
  selected,
  pageHref,
  onOpen,
}: {
  problem: StudyWorkProblem;
  selected: boolean;
  pageHref: (page: number) => string;
  onOpen: () => void;
}) {
  return (
    <li className={selected ? "study-guides-problem is-selected" : "study-guides-problem"}>
      <div className="study-guides-problem-head">
        <button
          type="button"
          className="study-guides-problem-label study-guides-select"
          aria-pressed={selected}
          onClick={onOpen}
        >
          {problem.label}
        </button>
        {problem.has_guide ? <span className="study-guides-badge">解説あり</span> : null}
      </div>
      <span className="study-guides-problem-source">〔{problem.source}〕</span>
      <ProblemPageLinks problem={problem} pageHref={pageHref} compact />
    </li>
  );
}

/**
 * 問題のページへの導線である。問題文だけの一覧 (list_page) があればそれを「問題」にし、本文は「解説」にする。
 * ※なぜなら本文のページには解法の説明が載っていて、解く前に開くと答えの方向が見えてしまうためである。
 */
function ProblemPageLinks({
  problem,
  pageHref,
  compact,
}: {
  problem: StudyWorkProblem;
  pageHref: (page: number) => string;
  compact?: boolean;
}) {
  const items: { label: string; page: number }[] = [];
  if (problem.list_page) {
    items.push({ label: "問題", page: problem.list_page });
    items.push({ label: "解説", page: problem.page });
  } else {
    items.push({ label: "問題", page: problem.page });
  }
  if (problem.answer_page) items.push({ label: "解答", page: problem.answer_page });
  return (
    <span className={compact ? "study-guides-problem-links" : "study-guides-page-chips"}>
      {items.map((it) => (
        <Link
          key={it.label}
          to={pageHref(it.page)}
          className={compact ? undefined : "study-guides-chip"}
        >
          {it.label} p.{it.page}
        </Link>
      ))}
    </span>
  );
}

/** 問1つの解説の本文 (問の名前・出典・原本への線・節)。面でも対話でも同じものを出す。 */
export function GuideBody({
  problem,
  guide,
  pageHref,
  titleId = "study-guides-title",
}: {
  problem: StudyWorkProblem;
  guide: GuideState;
  pageHref: (page: number) => string;
  /** 見出しの id (対話の中で使うときは面の見出しと重ならない id を渡す)。 */
  titleId?: string;
}) {
  return (
    <article aria-labelledby={titleId}>
      <h2 id={titleId} className="study-guides-title">
        {problem.label}
      </h2>
      <p className="study-guides-source">〔{problem.source}〕</p>
      {problem.summary ? <p className="study-guides-summary">{problem.summary}</p> : null}
      <p className="study-guides-open-row">
        <span className="study-guides-open-label">原本を開く</span>
        <ProblemPageLinks problem={problem} pageHref={pageHref} />
      </p>
      {!problem.has_guide || guide.status === "none" ? (
        <p className="study-muted">解説はまだありません。</p>
      ) : guide.status === "loading" ? (
        <p className="study-muted" role="status">
          読み込んでいます...
        </p>
      ) : guide.status === "error" ? (
        <p className="study-error" role="alert">
          解説を読み込めませんでした。
        </p>
      ) : (
        <GuideSections guide={guide.guide} pageHref={pageHref} />
      )}
    </article>
  );
}

function GuideSections({
  guide,
  pageHref,
}: {
  guide: StudyProblemGuide;
  pageHref: (page: number) => string;
}) {
  return (
    <>
      {guide.pages && guide.pages.length > 0 ? (
        <p className="study-guides-open-row" aria-label="解説が参照する原本のページ">
          <span className="study-guides-open-label">解説で参照するページ</span>
          <span className="study-guides-page-chips">
            {guide.pages.map((pg) => (
              <Link
                key={`${pg.label}-${pg.page}`}
                to={pageHref(pg.page)}
                className="study-guides-chip"
              >
                {pg.label} p.{pg.page}
              </Link>
            ))}
          </span>
        </p>
      ) : null}
      {guide.sections.map((s) => (
        <section key={s.id} className="study-guides-section">
          <h3>{s.heading}</h3>
          {s.blocks.map((b, i) =>
            b.kind === "md" ? (
              <div
                key={i}
                className="study-guides-md"
                dangerouslySetInnerHTML={{ __html: renderMathMarkdown(b.text ?? "") }}
              />
            ) : b.kind === "answer" ? (
              <details key={i} className="study-guides-answer">
                <summary className="study-guides-answer-summary">
                  <span className="study-guides-answer-tag">模範解答</span>
                  <span className="study-guides-answer-title">{b.title || DEFAULT_ANSWER_TITLE}</span>
                </summary>
                <div
                  className="study-guides-md study-guides-answer-body"
                  dangerouslySetInnerHTML={{ __html: renderMathMarkdown(b.text ?? "") }}
                />
              </details>
            ) : b.plot ? (
              <FunctionPlot key={i} spec={toPlotSpec(b.plot)} />
            ) : null,
          )}
        </section>
      ))}
      <p className="study-guides-provenance">
        この解説は原本の解法を参考に独自に書き直したものです
        {guide.source_ref.title ? `。出典は『${guide.source_ref.title}』` : ""}
        {guide.source_ref.pages && guide.source_ref.pages.length > 0
          ? ` p.${guide.source_ref.pages.join(", ")}`
          : ""}
        。
      </p>
    </>
  );
}
